"use client";

import { useMemo } from "react";
import type { CourseFlags } from "@/types/elearning";

/**
 * Configuración de vista derivada de las banderas del curso.
 * Resuelve la matriz de la especificación (§3 del backend) en un solo lugar:
 * las pantallas consumen esta config y no repiten la lógica de flags.
 */
export interface CourseViewConfig {
  /** El módulo muestra el paso "Quiz" (con avance restringido se habilita al completar el video; ver §7.2). */
  muestraQuiz: boolean;
  /** El curso muestra el bloque "Examen final" (fuera del alcance ANSV actual). */
  muestraExamenFinal: boolean;
  /** Se muestra la pantalla/acceso de certificado. */
  muestraCertificado: boolean;
}

/**
 * @function useCourseConfig
 * @description Deriva la configuración de la UI a partir de las tres banderas del
 * curso (`tiene_quiz_modulos`, `tiene_examen_final`, `emite_certificado`).
 * La UI se ARMA según el curso; nunca asume una combinación fija.
 *
 * @param {Partial<CourseFlags> | null} [flags] - Banderas del curso (o undefined mientras carga).
 * @returns {CourseViewConfig} La configuración de vista derivada.
 */
const useCourseConfig = (
  flags?: Partial<CourseFlags> | null
): CourseViewConfig =>
  useMemo(() => {
    const quiz = !!flags?.hasModuleQuizzes;
    const examen = !!flags?.hasFinalExam;
    return {
      muestraQuiz: quiz,
      muestraExamenFinal: examen,
      muestraCertificado: !!flags?.issuesCertificate
    };
  }, [flags?.hasModuleQuizzes, flags?.hasFinalExam, flags?.issuesCertificate]);

export default useCourseConfig;
